import {Button} from '../../ui-components/Button/button';
import {
    CancelIcon,
    CheckIcon,
    CropIcon,
    LockIconSmall,
    MaximiseIcon,
    MinimiseIcon,
    RemoveBgIcon,
    RotateLeft,
    RotateRight,
} from '../../ui-components/Iconpack';
import Tracker from '../../lib/analytics/tracker';
import {EventNames} from '../../lib/analytics/events';
import ImageRenderer from '../image-renderer/ImageRenderer';
import React, {ReactNode, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ReactCrop, {type Crop, PercentCrop} from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';
import {centerAspectCrop} from '../../utils/media';
import CircularProgressBar from '../../ui-components/loaders/CircularProgressLoader';

interface TransformationImageAreaProps {
    isLoading: boolean;
    allowBackgroundRemoval: boolean;
    isBgRemoved: boolean;
    svgOrGif?: boolean;
    cdnUrl: string;
    modifiedCdnUrl: string;
    backgroundRemoveActions: () => void;
    aspectRatio?: number;
    onCropConfirm: (xPercent: number, yPercent: number, wPercent: number, hPercent: number) => void;
    onCropCancel: () => void;
    onRotate: (angle: number) => void;
    rotation: number;
}

export default function TransformationImageArea({
    isLoading,
    allowBackgroundRemoval,
    isBgRemoved,
    onCropConfirm,
    svgOrGif,
    cdnUrl,
    modifiedCdnUrl,
    backgroundRemoveActions,
    aspectRatio,
    onCropCancel,
    rotation,
    onRotate,
}: TransformationImageAreaProps) {
    const navigate = useNavigate();
    const [zoomPercentage, setZoomPercentage] = useState(100);
    const [intermediateCrop, setIntermediateCrop] = useState<Crop>();
    const [finalCrop, setFinalCrop] = useState<Crop>();
    const [cropMode, setCropMode] = useState(false);
    const [imgWidth, setImgWidth] = useState(0);
    const [imgHeight, setImgHeight] = useState(0);
    const [ar, setAr] = useState(aspectRatio);
    const [progress, setProgress] = useState(false);
    const imageDimension = (width: number, height: number) => {
        setImgWidth(width);
        setImgHeight(height);
    };

    const handleZoomChange = (percentage: number) => {
        setZoomPercentage(percentage);
    };

    const setCropData = (c: PercentCrop) => {
        setIntermediateCrop(c);
    };

    const toggleCropMode = () => {
        setCropMode(!cropMode);
    };

    const handleZoomIn = () => {
        window.dispatchEvent(new Event('zoomInClicked'));
    };

    const handleZoomOut = () => {
        window.dispatchEvent(new Event('zoomOutClicked'));
    };

    const handleZoomReset = () => {
        window.dispatchEvent(new Event('zoomResetClicked'));
    };
    useEffect(() => {
        if (aspectRatio) {
            setCropMode(true);
            setIntermediateCrop(centerAspectCrop(imgWidth, imgHeight, aspectRatio));
        }
    }, [aspectRatio]);

    useEffect(() => {
        return () => {
            window.removeEventListener('click', handleZoomIn);
            window.removeEventListener('click', handleZoomOut);
            window.removeEventListener('click', handleZoomReset);
        };
    }, []);

    useEffect(() => {
        if (finalCrop && intermediateCrop) {
            const {x, y, width, height} = intermediateCrop;
            onCropConfirm(x, y, width, height);
        }
    }, [finalCrop]);

    useEffect(() => {
        if (aspectRatio) {
            setAr(aspectRatio);
        }
    }, [aspectRatio]);

    useEffect(() => {
        if (isLoading) {
            setProgress(true);
        } else {
            setTimeout(() => {
                setProgress(false);
            }, 500);
        }
    }, [isLoading]);

    const Btn = (props: {onClick: () => void; children?: ReactNode}) => {
        return (
            <div className='text-inter flex cursor-pointer items-center p-1' onClick={props.onClick}>
                {props.children}
            </div>
        );
    };

    return (
        <div className={'flex w-full flex-col items-center'}>
            {!cropMode ? (
                <div className='my-5 flex gap-1 rounded-[0.5rem] bg-[#1D2939] px-1 md:gap-4 md:px-3'>
                    <div className={'flex'}>
                        <Btn
                            onClick={() => {
                                handleZoomOut();
                            }}>
                            <MinimiseIcon />
                        </Btn>
                        <span className='flex items-center px-1 text-[12px] font-[500] text-[#D0D5DD] md:text-[14px]'>
                            {zoomPercentage.toFixed(0)}%
                        </span>
                        <Btn
                            onClick={() => {
                                handleZoomIn();
                            }}>
                            <MaximiseIcon />
                        </Btn>
                    </div>

                    {!svgOrGif && (
                        <Btn
                            onClick={() => {
                                setAr(1);
                                toggleCropMode();
                                setIntermediateCrop(centerAspectCrop(imgWidth, imgHeight, 1));
                            }}>
                            <div className={'flex gap-2 pt-[4px]'}>
                                <CropIcon color={'#D0D5DD'} />
                                <span className={'flex items-center text-[12px] text-white md:text-[14px]'}>Crop</span>
                            </div>
                        </Btn>
                    )}
                    {!svgOrGif && (
                        <>
                            <div className='relative'>
                                <Button
                                    variant='secondary'
                                    size='sm'
                                    className='relative border-transparent bg-[#1D2939] text-[12px] font-[500] text-[#D0D5DD] hover:bg-transparent md:text-[14px]'
                                    icon
                                    disabled={!allowBackgroundRemoval}
                                    onClick={() => {
                                        if (isBgRemoved) {
                                            Tracker.trackEvent(EventNames.TRANSFORMATION_BACKGROUND_REMOVED);
                                        } else {
                                            Tracker.trackEvent(EventNames.TRANSFORMATION_BACKGROUND_RESTORED);
                                        }
                                        backgroundRemoveActions();
                                    }}>
                                    <RemoveBgIcon />
                                    <span className={'flex md:hidden'}>{isBgRemoved ? 'BG Restore' : 'Remove BG'}</span>
                                    <span className={'hidden md:flex'}>
                                        {isBgRemoved ? 'Restore Original Background' : 'Remove Background'}
                                    </span>
                                </Button>
                                {!allowBackgroundRemoval && (
                                    <Button
                                        className='absolute left-[50px] top-[-5px] h-[10px] w-[88px] gap-2 rounded-[33px] bg-gradient-to-r from-[#FFB800] to-[#FFF500] text-[9px] font-[700] text-[#000000]'
                                        onClick={() => navigate('/settings/plans')}>
                                        <LockIconSmall />
                                        Upgrade
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                    <div
                        className={'flex cursor-pointer items-center justify-center'}
                        onClick={() => {
                            if (rotation === 0) {
                                onRotate(270);
                            } else {
                                onRotate(rotation - 90);
                            }
                        }}>
                        <RotateLeft />
                        <span className='hidden px-1 pt-1 text-[12px] font-[500] text-[#D0D5DD] md:flex md:text-[14px]'>
                            Rotate left
                        </span>
                        <span className='flex px-1 pt-1 text-[12px] font-[500] text-[#D0D5DD] md:hidden md:text-[14px]'>
                            RL
                        </span>
                    </div>
                    <div
                        className={'flex cursor-pointer items-center justify-center'}
                        onClick={() => {
                            if (rotation === 270) {
                                onRotate(0);
                            } else {
                                onRotate(rotation + 90);
                            }
                        }}>
                        <span className='hidden px-1 pt-1 text-[12px] font-[500] text-[#D0D5DD] md:flex md:text-[14px]'>
                            Rotate Right
                        </span>
                        <span className='flex px-1 pl-1 pt-1 text-[12px] font-[500] text-[#D0D5DD] md:hidden md:text-[14px]'>
                            RR
                        </span>
                        <RotateRight />
                    </div>
                </div>
            ) : (
                <div className='my-5 flex gap-5'>
                    <div
                        className='cursor-pointer'
                        onClick={() => {
                            toggleCropMode();
                            setFinalCrop(intermediateCrop);
                        }}>
                        <CheckIcon />
                    </div>
                    <div
                        className='cursor-pointer'
                        onClick={() => {
                            toggleCropMode();
                            onCropCancel();
                        }}>
                        <CancelIcon />
                    </div>
                </div>
            )}
            {progress ? (
                <div className={'flex text-white md:my-20'}>
                    <CircularProgressBar completed={!isLoading} />
                </div>
            ) : (
                <>
                    {cropMode ? (
                        <ReactCrop
                            className='max-h-[75vh] w-auto md:max-h-[75vh]'
                            aspect={cropMode ? (ar === 1 ? undefined : ar) : undefined}
                            ruleOfThirds
                            crop={intermediateCrop}
                            onChange={(c, pc) => setCropData(pc)}>
                            <ImageRenderer
                                imageDimension={imageDimension}
                                cropMode={cropMode}
                                cdnUrl={cdnUrl}
                                modifiedCdnUrl={modifiedCdnUrl}
                                onZoomChange={handleZoomChange}
                            />
                        </ReactCrop>
                    ) : (
                        <ImageRenderer
                            imageDimension={imageDimension}
                            cropMode={cropMode}
                            cdnUrl={cdnUrl}
                            modifiedCdnUrl={modifiedCdnUrl}
                            onZoomChange={handleZoomChange}
                        />
                    )}
                </>
            )}
        </div>
    );
}
